<template>
  <div class="roles-management">
    <!-- 🎯 Header -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="pa-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white">
        <div class="d-flex align-center">
          <v-icon icon="mdi-account-group" size="32" class="me-3" />
          <div>
            <h2 class="text-h4 mb-0">👥 Gestión de Roles</h2>
            <p class="text-subtitle-1 mb-0 opacity-90">Sistema de roles y permisos MGI</p>
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- 🔍 Role Filter -->
    <v-card class="mb-4" elevation="1">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedRole"
              :items="availableRoles"
              item-title="label"
              item-value="value"
              label="Seleccionar Rol"
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-account-filter"
            />
          </v-col>
          <v-col cols="12" md="8">
            <div class="d-flex justify-end">
              <v-btn
                variant="outlined"
                color="primary"
                prepend-icon="mdi-refresh"
                @click="refreshRoles"
              >
                Actualizar Roles
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 📊 Roles Grid -->
    <v-row>
      <v-col
        v-for="role in filteredRoles"
        :key="role.id"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          :class="['role-card', `role-${role.type}`]"
          elevation="2"
          @click="selectRole(role)"
        >
          <v-card-title class="pa-4">
            <div class="d-flex align-center">
              <v-avatar :color="role.color" size="40" class="me-3">
                <v-icon :icon="role.icon" color="white" />
              </v-avatar>
              <div>
                <h3 class="text-h6 mb-0">{{ role.label }}</h3>
                <p class="text-caption mb-0 text-medium-emphasis">{{ role.department }}</p>
              </div>
            </div>
          </v-card-title>

          <v-card-text class="pa-4 pt-0">
            <p class="text-body-2 mb-3">{{ role.description }}</p>
            
            <!-- 🔑 Permissions -->
            <div class="mb-3">
              <p class="text-caption font-weight-bold mb-2">Permisos:</p>
              <v-chip-group>
                <v-chip
                  v-for="permission in role.permissions.slice(0, 3)"
                  :key="permission"
                  size="small"
                  variant="outlined"
                  :color="role.color"
                >
                  {{ permission }}
                </v-chip>
                <v-chip
                  v-if="role.permissions.length > 3"
                  size="small"
                  variant="text"
                  color="primary"
                >
                  +{{ role.permissions.length - 3 }} más
                </v-chip>
              </v-chip-group>
            </div>

            <!-- 📈 Stats -->
            <div class="d-flex justify-space-between text-caption">
              <span><strong>{{ role.userCount }}</strong> usuarios</span>
              <span><strong>{{ role.permissions.length }}</strong> permisos</span>
            </div>
          </v-card-text>

          <v-card-actions class="pa-4 pt-0">
            <v-btn
              variant="outlined"
              size="small"
              @click.stop="editRole(role)"
            >
              Editar
            </v-btn>
            <v-spacer />
            <v-btn
              variant="text"
              size="small"
              :color="role.color"
              @click.stop="testRole(role)"
            >
              Probar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- 🔄 Back to Dev Center -->
    <v-card class="mt-6" elevation="1">
      <v-card-actions class="pa-4">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-arrow-left"
          @click="$router.push('/dev-center')"
        >
          Volver al Dev Center
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          @click="createNewRole"
        >
          Crear Nuevo Rol
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'

// 🎯 Types
interface Role {
  id: string
  label: string
  value: string
  type: string
  department: string
  description: string
  icon: string
  color: string
  permissions: string[]
  userCount: number
}

// 🔧 Reactive state
const store = useMainStore()
const selectedRole = ref<string>('')

// 📊 Mock data
const roles = ref<Role[]>([
  {
    id: '1',
    label: 'Super Admin',
    value: 'super_admin',
    type: 'admin',
    department: 'Administración',
    description: 'Acceso completo al sistema MGI',
    icon: 'mdi-shield-crown',
    color: 'red',
    permissions: ['create', 'read', 'update', 'delete', 'manage_users', 'system_config'],
    userCount: 3
  },
  {
    id: '2',
    label: 'Gerente Regional',
    value: 'gerente_regional',
    type: 'manager',
    department: 'Gestión',
    description: 'Supervisión regional y análisis estratégico',
    icon: 'mdi-account-tie',
    color: 'purple',
    permissions: ['read', 'update', 'create_reports', 'manage_staff'],
    userCount: 8
  },
  {
    id: '3',
    label: 'Supervisor de Zona',
    value: 'supervisor_zona',
    type: 'supervisor',
    department: 'Operaciones',
    description: 'Control operativo y coordinación de equipos',
    icon: 'mdi-account-supervisor',
    color: 'blue',
    permissions: ['read', 'update', 'assign_tasks', 'validate_incidents'],
    userCount: 15
  },
  {
    id: '4',
    label: 'Operador de Rutas',
    value: 'operador_rutas',
    type: 'operator',
    department: 'Recolección',
    description: 'Ejecutor de rutas y reportes de campo',
    icon: 'mdi-truck',
    color: 'orange',
    permissions: ['read', 'create_reports', 'update_status'],
    userCount: 45
  },
  {
    id: '5',
    label: 'Empleado de Agencia',
    value: 'empleado_agencia',
    type: 'employee',
    department: 'Atención al Cliente',
    description: 'Atención ciudadana y gestión de incidencias',
    icon: 'mdi-account-tie-hat',
    color: 'green',
    permissions: ['read', 'create_incidents', 'update_incidents'],
    userCount: 32
  },
  {
    id: '6',
    label: 'Analista Central',
    value: 'analista_central',
    type: 'analyst',
    department: 'Business Intelligence',
    description: 'Análisis de datos y generación de insights',
    icon: 'mdi-chart-line',
    color: 'teal',
    permissions: ['read', 'generate_analytics', 'export_data'],
    userCount: 12
  }
])

const availableRoles = computed(() => [
  { label: 'Todos los Roles', value: '' },
  { label: 'Administración', value: 'admin' },
  { label: 'Gestión', value: 'manager' },
  { label: 'Supervisión', value: 'supervisor' },
  { label: 'Operación', value: 'operator' },
  { label: 'Empleados', value: 'employee' },
  { label: 'Análisis', value: 'analyst' }
])

const filteredRoles = computed(() => {
  if (!selectedRole.value) return roles.value
  return roles.value.filter(role => role.type === selectedRole.value)
})

// 🎯 Methods
const selectRole = (role: Role) => {
  console.log('🎯 Rol seleccionado:', role)
  store.showNotification({
    type: 'info',
    title: 'Rol Seleccionado',
    message: `Has seleccionado el rol: ${role.label}`
  })
}

const editRole = (role: Role) => {
  console.log('✏️ Editando rol:', role)
  store.showNotification({
    type: 'info',
    title: 'Editar Rol',
    message: `Editando permisos para: ${role.label}`
  })
}

const testRole = (role: Role) => {
  console.log('🧪 Probando rol:', role)
  store.showNotification({
    type: 'success',
    title: 'Modo Prueba',
    message: `Simulando sesión como: ${role.label}`
  })
}

const createNewRole = () => {
  console.log('➕ Creando nuevo rol')
  store.showNotification({
    type: 'info',
    title: 'Nuevo Rol',
    message: 'Abriendo formulario de creación de rol'
  })
}

const refreshRoles = () => {
  console.log('🔄 Actualizando roles')
  store.showNotification({
    type: 'success',
    title: 'Actualizado',
    message: 'Lista de roles actualizada correctamente'
  })
}

// 🎬 Lifecycle
onMounted(() => {
  console.log('👥 Vista de gestión de roles cargada')
})
</script>

<style scoped>
.roles-management {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.role-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-left: 4px solid transparent;
}

.role-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.role-card.role-admin {
  border-left-color: #f44336;
}

.role-card.role-manager {
  border-left-color: #9c27b0;
}

.role-card.role-supervisor {
  border-left-color: #2196f3;
}

.role-card.role-operator {
  border-left-color: #ff9800;
}

.role-card.role-employee {
  border-left-color: #4caf50;
}

.role-card.role-analyst {
  border-left-color: #009688;
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #9c27b0 0%, #673ab7 100%);
}
</style>
